import React from 'react';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { Plus, Trash2 } from 'lucide-react';

import type { TouringDay } from 'src/services/api';
import TimeSelect from 'src/components/property-touring-hours/TimeSelect';
import {
    type DayForm,
    type WindowForm,
    days,
    getWindowsError,
} from 'src/components/property-touring-hours/touring-hours-form';
import classes from 'src/components/property-touring-hours/PropertyTouringHoursSection.module.css';

type Props = {
    weekly: Record<TouringDay, DayForm>;
    isReadOnly: boolean;
    onToggleDay: (day: TouringDay, isOpen: boolean) => void;
    onUpdateWindow: (day: TouringDay, index: number, update: Partial<WindowForm>) => void;
    onAddWindow: (day: TouringDay) => void;
    onRemoveWindow: (day: TouringDay, index: number) => void;
};

export default function WeeklyHoursSchedule({
    weekly,
    isReadOnly,
    onToggleDay,
    onUpdateWindow,
    onAddWindow,
    onRemoveWindow,
}: Props) {
    return (
        <div className={classes.schedule}>
            {days.map(({ shortLabel, value }) => {
                const day = weekly[value];
                const isOpen = day.windows.length > 0;
                const windowsError = getWindowsError(day.windows);
                return (
                    <div className={classes.dayRow} key={value}>
                        <div className={classes.dayPrefix}>
                            <Avatar
                                className={`${classes.dayAvatar} ${isOpen ? classes.dayAvatarOpen : classes.dayAvatarClosed}`}
                            >
                                {shortLabel}
                            </Avatar>
                            <Switch
                                checked={isOpen}
                                onChange={(_, checked) => onToggleDay(value, checked)}
                                size="small"
                                disabled={isReadOnly}
                                inputProps={{ 'aria-label': `Open ${shortLabel}` }}
                                data-testid={`toggle-tour-availability-${shortLabel.toLowerCase()}-switch`}
                            />
                        </div>
                        <div className={classes.windowList}>
                            {day.windows.length > 0 ? day.windows.map((window, index) => (
                                <div className={classes.windowRow} key={`${value}-${index}`}>
                                    <TimeSelect
                                        value={window.open_time}
                                        onChange={(time) => onUpdateWindow(value, index, { open_time: time })}
                                        testId={`tour-availability-${shortLabel.toLowerCase()}-open-${index}`}
                                        disabled={isReadOnly}
                                    />
                                    <TimeSelect
                                        value={window.close_time}
                                        onChange={(time) => onUpdateWindow(value, index, { close_time: time })}
                                        testId={`tour-availability-${shortLabel.toLowerCase()}-close-${index}`}
                                        disabled={isReadOnly}
                                    />
                                    {!isReadOnly && (
                                        <div className={classes.windowActions}>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                onClick={() => onAddWindow(value)}
                                                data-testid={`add-tour-availability-${shortLabel.toLowerCase()}-window-button`}
                                            >
                                                <Plus size={16} />
                                            </IconButton>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                onClick={() => onRemoveWindow(value, index)}
                                                data-testid={`delete-tour-availability-${shortLabel.toLowerCase()}-window-button`}
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div className={classes.emptyWindowRow}>
                                    <Typography variant="body2" color="text.secondary">Closed</Typography>
                                </div>
                            )}
                            {windowsError && (
                                <Typography variant="caption" color="error" data-testid={`tour-availability-${shortLabel.toLowerCase()}-error`}>
                                    {windowsError}
                                </Typography>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
